import React, { useState } from "react";
import { Accordion, Card } from "react-bootstrap";
import styles from '../HelpSection.module.css'

const HelpRateCardScreen = () => {
  const [activeKey, setActiveKey] = useState("0");

  return (
    <div className={styles.HelpAddLanes}>
      {/* ⚠️ Note Section */}
      <div className={styles.HelpShortlistLsp}>
        <div className="alert alert-warning my-2">
          <h6>Note:</h6>
          <ul className="m-0 pl-2">
            <li>This is the main rate card screen where the transporter can submit freight rates for various lanes.</li>
            <li>It shows real-time details about the procurement round — including the round number, total rounds, and a live countdown timer till submission ends.</li>
            <li>Transporters can input their base freight, apply bundle discounts, check rankings, download/upload rate cards, and finalize submission from here.</li>
          </ul>
        </div>
      </div>

      {/* 📚 Accordion Section */}
      <Accordion activeKey={activeKey} onSelect={(e) => setActiveKey(e)}>
        {/* 1. Rate Table */}
        <Card className={styles.accordionCard}>
          <Accordion.Toggle as={Card.Header} eventKey="0">
            <div className="d-flex justify-content-between align-items-baseline w-100">
              <span style={{ fontWeight: "bold" }}>Rate Table</span>
              <span style={{ fontWeight: "bolder" }}>{activeKey === "0" ? "▼" : "▲"}</span>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <ul className="list-group">
                <li className="list-group-item">This is where you enter your quote for each lane — including base freight rate and transit time.</li>
                <li className="list-group-item">Columns like <strong>Lane Spend</strong>, <strong>Previous Rate</strong>, <strong>Bundle No</strong>, <strong>Bundle Rate</strong>, and <strong>Rank</strong> give full visibility.</li>
                <li className="list-group-item">Use pagination to navigate through lanes if they exceed one page.</li>
                <li className="list-group-item">Disabled cells and tooltips indicate locked/uneditable fields.</li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        {/* 2. Submit Rates */}
        <Card className={styles.accordionCard}>
          <Accordion.Toggle as={Card.Header} eventKey="1">
            <div className="d-flex justify-content-between align-items-baseline w-100">
              <span style={{ fontWeight: "bold" }}>Submit Rates</span>
              <span style={{ fontWeight: "bolder" }}>{activeKey === "1" ? "▼" : "▲"}</span>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="1">
            <Card.Body>
              <ul className="list-group">
                <li className="list-group-item">
                  Click <button className="btn btn-sm btn-success">Submit Rates</button> to open the submission popup.
                </li>
                <li className="list-group-item">
                  Accept terms & conditions to finalize your rates. The T&Cs can also be downloaded.
                </li>
                <li className="list-group-item">
                  <button className="btn btn-sm btn-success bg-transparent text-success">Save Draft</button> saves without submitting.
                </li>
                <li className="list-group-item">
                  <button className="btn btn-sm bg-transparent btn-danger text-danger">Clear Rates</button> wipes everything. Use with caution.
                </li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        {/* 3. Add Bundle Discounts */}
        <Card className={styles.accordionCard}>
          <Accordion.Toggle as={Card.Header} eventKey="2">
            <div className="d-flex justify-content-between align-items-baseline w-100">
              <span style={{ fontWeight: "bold" }}>Add Bundle Discounts</span>
              <span style={{ fontWeight: "bolder" }}>{activeKey === "2" ? "▼" : "▲"}</span>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="2">
            <Card.Body>
              <ul className="list-group">
                <li className="list-group-item">Go to the right-side "Lane Bundles" section.</li>
                <li className="list-group-item">
                  Use <button className="bg-dark btn-sm"><i className="fa fa-minus text-white"></i></button> and <button className="bg-dark btn-sm"><i className="fa fa-plus text-white"></i></button> to adjust by 0.5%.
                </li>
                <li className="list-group-item">Or enter values manually in the input box.</li>
                <li className="list-group-item">
                  Click <button className="btn btn-sm btn-success">Save Bundles</button> to store the bundle settings.
                </li>
                <li className="list-group-item">Assign saved bundles to lanes using the <strong>Bundle No</strong> dropdown in the table.</li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        {/* 4. Download Rate Card */}
        <Card className={styles.accordionCard}>
          <Accordion.Toggle as={Card.Header} eventKey="3">
            <div className="d-flex justify-content-between align-items-baseline w-100">
              <span style={{ fontWeight: "bold" }}>Download Rate Card</span>
              <span style={{ fontWeight: "bolder" }}>{activeKey === "3" ? "▼" : "▲"}</span>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="3">
            <Card.Body>
              <ul className="list-group">
                <li className="list-group-item">Click "Download Rate Card" to get the Excel file.</li>
                <li className="list-group-item">Only unlocked fields (freight rates etc.) can be edited.</li>
                <li className="list-group-item">Locked columns (like Origin-Destination) are read-only.</li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        {/* 5. Upload Rate Card */}
        <Card className={styles.accordionCard}>
          <Accordion.Toggle as={Card.Header} eventKey="4">
            <div className="d-flex justify-content-between align-items-baseline w-100">
              <span style={{ fontWeight: "bold" }}>Upload Rate Card</span>
              <span style={{ fontWeight: "bolder" }}>{activeKey === "4" ? "▼" : "▲"}</span>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="4">
            <Card.Body>
              <ul className="list-group">
                <li className="list-group-item">Upload your updated Excel to auto-fill the table with rates.</li>
                <li className="list-group-item">Ensure formatting is untouched to avoid errors.</li>
                <li className="list-group-item">You can still edit after uploading, before final submission.</li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        {/* 6. Rate Card Header */}
        <Card className={styles.accordionCard}>
          <Accordion.Toggle as={Card.Header} eventKey="5">
            <div className="d-flex justify-content-between align-items-baseline w-100">
              <span style={{ fontWeight: "bold" }}>Rate Card Header</span>
              <span style={{ fontWeight: "bolder" }}>{activeKey === "5" ? "▼" : "▲"}</span>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="5">
            <Card.Body>
              <ul className="list-group">
                <li className="list-group-item">Top bar shows Procurement Title, Exit & Activity buttons.</li>
                <li className="list-group-item">Key stats include:
                  <ul>
                    <li><strong>Bundled Rate</strong></li>
                    <li><strong>Submitted Rate</strong></li>
                    <li><strong>Drafted Rate</strong></li>
                    <li><strong>Total Spend</strong></li>
                  </ul>
                </li>
                <li className="list-group-item">Live countdown timer displays how much time is left.</li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        {/* 7. Filter Lanes */}
        <Card className={styles.accordionCard}>
          <Accordion.Toggle as={Card.Header} eventKey="6">
            <div className="d-flex justify-content-between align-items-baseline w-100">
              <span style={{ fontWeight: "bold" }}>Filter Lanes</span>
              <span style={{ fontWeight: "bolder" }}>{activeKey === "6" ? "▼" : "▲"}</span>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="6">
            <Card.Body>
              <ul className="list-group">
                <li className="list-group-item">Click <button className="btn btn-sm text-white" style={{ backgroundColor: "#393185" }}><i className="fa fa-filter"></i></button> to open filters.</li>
                <li className="list-group-item">Filter by:
                  <ul>
                    <li><strong>Truck Type</strong></li>
                    <li><strong>Volume</strong></li>
                    <li><strong>Lot</strong></li>
                    <li><strong>Location</strong></li>
                    <li><strong>Bundle Applied</strong></li>
                  </ul>
                </li>
                <li className="list-group-item">Click <strong>Apply</strong> to filter or <strong>Clear</strong> to reset.</li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        {/* 8. Nodal Child */}
        <Card className={styles.accordionCard}>
          <Accordion.Toggle as={Card.Header} eventKey="7">
            <div className="d-flex justify-content-between align-items-baseline w-100">
              <span style={{ fontWeight: "bold" }}>Nodal Child</span>
              <span style={{ fontWeight: "bolder" }}>{activeKey === "7" ? "▼" : "▲"}</span>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="7">
            <Card.Body>
              <ul className="list-group">
                <li className="list-group-item">Nodal Child mode groups lanes by lots.</li>
                <li className="list-group-item">The parent lane has a <i className="fa fa-code-fork"></i> icon and is highlighted.</li>
                <li className="list-group-item">You only enter freight for the parent — child lanes auto-calculate from it.</li>
                <li className="list-group-item">Keeps everything consistent and saves manual effort.</li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </div>
  );
};

export default HelpRateCardScreen;
